<template>
  <v-card width="100%" elevation="0" class="ml-5">
    <v-container>
      <v-row>
        <span style="color: #5757ff; width: 100%; text-align: center">
          Note: Propagation accuracy of a TLE may degrade quickly away from its
          definition date. This is especially true for LEO objects or
          maneuvering spacecrafts.
        </span>
      </v-row>
      <v-row>
        <v-col>
          <TLEDecoder
            v-on:expand="isExpanded = $event"
            :tle.sync="firstTLE"
            v-on:updateDate="firstTLEDate = $event"
            :isExpanded="isExpanded"
            :expandDisabled="false"
          ></TLEDecoder>
        </v-col>
        <v-col>
          <TLEDecoder
            v-on:expand="isExpanded = $event"
            :tle.sync="secondTLE"
            v-on:updateDate="secondTLEDate = $event"
            :isExpanded="isExpanded"
            :expandDisabled="false"
          ></TLEDecoder>
        </v-col>
      </v-row>
      <template
        style="width: 100%"
        v-if="this.firstTLE != null && this.secondTLE != null"
      >
        <v-row v-if="errorMessageDate">
          <span class="red--text" style="width: 100%; text-align: center">{{
            this.errorMessageDate
          }}</span>
        </v-row>
        <v-row v-if="!errorMessageDate && warningMessageDate">
          <span class="orange--text" style="width: 100%; text-align: center">{{
            this.warningMessageDate
          }}</span>
        </v-row>
        <v-row
          v-if="!this.backendErrorMessage"
          style="width: 100%; justify-content: center"
          ><div id="plotDiv" style="width: 100%; height: 400px"></div>
        </v-row>
        <v-row v-else style="justify-content: center"
          ><div
            class="red--text"
            style="
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 400px;
            "
          >
            {{ backendErrorMessage }}
          </div>
        </v-row>
        <v-row style="display: flex; justify-content: space-evenly">
          <v-col cols="5">
            <exoDatetimePicker
              outlined
              dense
              :label="'Initial Date'"
              v-model="initialDate"
              @input="updateDates"
            >
            </exoDatetimePicker>
          </v-col>
          <v-col cols="1" style="display: flex; justify-content: center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="resetDatesAndUpdate"
                >
                  <v-icon dark class="ma-0">refresh</v-icon>
                </v-btn>
              </template>
              <span>Reset interval</span>
            </v-tooltip>
          </v-col>
          <v-col cols="5">
            <exoDatetimePicker
              outlined
              dense
              :label="'Final Date'"
              v-model="finalDate"
              @input="updateDates"
            >
            </exoDatetimePicker>
          </v-col>
        </v-row>
      </template>
      <v-row
        v-if="this.firstTLE == null || this.secondTLE == null"
        justify-center
        align-self-center
        style="
          padding-left: 15px;
          padding-right: 60px;
          padding-top: 110px;
          padding-bottom: 150px;
        "
      >
        <span class="grey--text" style="width: 100%; text-align: center"
          >Enter two TLEs to display the separation between the two space
          objects over a specified duration</span
        >
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import dataDescription from '../../tools/dataDescription.json'
import dataTools from '../../tools/data.js'
import TLEDecoder from './TLEDecoder.vue'
import Plotly from 'plotly.js-dist'
import exoDatetimePicker from '@/components/Forms/dateTimePicker.vue'
import moment from 'moment'

export default {
  name: 'TLEDistance',

  components: {
    TLEDecoder,
    exoDatetimePicker
  },

  data() {
    return {
      initialDate: null,
      finalDate: null,
      firstTLE: null,
      secondTLE: null,
      firstTLEDate: null,
      secondTLEDate: null,
      plotLayout: {
        plot_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        paper_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        font: { color: this.$vuetify.theme.dark ? 'white' : '#121212' },
        xaxis: {
          title: { text: 'Time (Days)' }
        },
        yaxis: {
          title: { text: 'Distance between the space objects (km)' },
          tickformat: '.3g'
        },
        margin: { t: 0 }
      },
      plotConfig: { responsive: true },
      isExpanded: false,
      errorMessageDate: null,
      backendErrorMessage: null,
      warningMessageDate: null
    }
  },

  async mounted() {},

  watch: {
    firstTLE() {
      this.resetDatesAndUpdate()
    },
    secondTLE() {
      this.resetDatesAndUpdate()
    }
  },

  computed: {
    isValidTLEs() {
      return this.firstTLE && this.secondTLE
    }
  },

  methods: {
    resetDates() {
      this.initialDate = null
      this.finalDate = null
      this.errorMessageDate = null
      this.warningMessageDate = null
    },
    updateDates() {
      this.errorMessageDate = null
      this.warningMessageDate = null
      this.tryUpdate()
    },
    resetDatesAndUpdate() {
      this.resetDates()
      this.tryUpdate()
    },
    isValidDates() {
      if (this.initialDate == null || this.finalDate == null) {
        return true
      }
      if (!moment.utc(this.initialDate).isBefore(moment.utc(this.finalDate))) {
        this.errorMessageDate =
          'The initial date cannot be after the final date'
        return false
      } else if (
        moment.utc(this.finalDate).diff(moment.utc(this.initialDate), 'days') >
        30
      ) {
        this.errorMessageDate =
          'The time period between the two dates cannot be longer than 30 days'
        return false
      }
      return true
    },

    tryUpdate() {
      if (this.isValidTLEs && this.isValidDates()) {
        this.updatePlot()
      }
    },

    warnIfInaccurate() {
      const THRESHOLD_DAYS = 10
      const maxTLEDate = moment.max(
        moment.utc(this.firstTLEDate),
        moment.utc(this.secondTLEDate)
      )
      const minTLEDate = moment.min(
        moment.utc(this.firstTLEDate),
        moment.utc(this.secondTLEDate)
      )
      const maxLimitDate = maxTLEDate.add(THRESHOLD_DAYS, 'days')
      const minLimitDate = minTLEDate.subtract(THRESHOLD_DAYS, 'days')
      if (
        moment.utc(this.initialDate).isBefore(minLimitDate) ||
        moment.utc(this.finalDate).isAfter(maxLimitDate)
      ) {
        this.warningMessageDate = `The requested time interval is more than ${THRESHOLD_DAYS} days away from one of the TLEs. Results may be inaccurate.`
      }
    },

    async updatePlot() {
      const tle = {
        firstOrbitDefinitionType: 'TLE',
        secondOrbitDefinitionType: 'TLE',
        firstTLE: this.firstTLE,
        secondTLE: this.secondTLE,
        initialPropagationDate: this.initialDate,
        finalPropagationDate: this.finalDate,
        timeStep: 2
      }
      this.backendErrorMessage = null
      const { data, error } = await this.$http
        .post('/physics/relativedistance', tle)
        .then(function (response) {
          return { data: response.data, error: null }
        })
        .catch((e) => {
          if (
            e.response &&
            e.response.status === 400 &&
            e.response.data.startsWith('Invalid definition')
          ) {
            return {
              data: null,
              error: e.response.data.match(
                /Invalid definition\. (?<message>.*)/
              )?.groups?.message
            }
          } else {
            return {
              data: null,
              error:
                'An error occured while trying to communicate with the server'
            }
          }
        })

      const plotDiv = document.getElementById('plotDiv')
      Plotly.purge(plotDiv)
      this.backendErrorMessage = error
      if (this.backendErrorMessage == null) {
        Plotly.newPlot(
          plotDiv,
          [
            {
              x: data.propagationDates,
              y: data.relativeDistanceEphemerides.map((x) => x / 1000)
            }
          ],
          this.plotLayout,
          this.plotConfig
        )
        this.initialDate = data.propagationDates[0]
        this.finalDate = data.propagationDates[data.propagationDates.length - 1]
        this.warnIfInaccurate()
      }
    }
  }
}
</script>
